import { defineStore } from 'pinia'
import { StorageSerializers, useLocalStorage } from '@vueuse/core'
import { $api } from '@/utils/api'
import type { User } from '@/stores/user'
import { type Rule, ability } from '@/plugins/casl/ability'
import avatar from '@images/avatars/avatar-2.png'
import { useSocket } from '@/composables/useSocket'
import { clearAllGlobalTimers } from '@/composables/useTimers'
import { tokenManager } from '@/utils/tokenManager'
import { authLogger } from '@/utils/logger'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  sessionId: string | null
  isForceLogoutModalVisible: boolean
  isLoggingOut: boolean
}

// ใช้ localStorage สำหรับทุกอย่าง (ไม่มี size limit)
// Helper functions for localStorage
const getAccessToken = (): string | null => {
  if (typeof window !== 'undefined')
    return useLocalStorage('accessToken').value

  return null
}

const setAccessToken = (token: string | null) => {
  if (typeof window !== 'undefined')
    useLocalStorage('accessToken').value = token
}

const getRefreshToken = (): string | null => {
  if (typeof window !== 'undefined')
    return useLocalStorage('refreshToken').value

  return null
}

const setRefreshToken = (token: string | null) => {
  if (typeof window !== 'undefined')
    useLocalStorage('refreshToken').value = token
}

const getSessionId = (): string | null => {
  if (typeof window !== 'undefined')
    return useLocalStorage('sessionId').value

  return null
}

const setSessionId = (sessionId: string | null) => {
  if (typeof window !== 'undefined')
    useLocalStorage('sessionId').value = sessionId
}

// Helper functions for localStorage with JSON serializer
const getUserData = (): User | null => {
  if (typeof window !== 'undefined') {
    const userDataStorage = useLocalStorage<User | null>(
      'userData',
      null,
      { serializer: StorageSerializers.object },
    )

    return userDataStorage.value
  }

  return null
}

const setUserData = (userData: User | null) => {
  if (typeof window !== 'undefined') {
    const userDataStorage = useLocalStorage<User | null>(
      'userData',
      null,
      { serializer: StorageSerializers.object },
    )
    userDataStorage.value = userData
  }
}

const getAbilityRules = (): Rule[] => {
  if (typeof window !== 'undefined') {
    const rulesStorage = useLocalStorage<Rule[]>(
      'userAbilityRules',
      [],
      { serializer: StorageSerializers.object },
    )

    return rulesStorage.value
  }

  return []
}

const setAbilityRules = (rules: Rule[]) => {
  if (typeof window !== 'undefined') {
    const rulesStorage = useLocalStorage<Rule[]>(
      'userAbilityRules',
      [],
      { serializer: StorageSerializers.object },
    )
    rulesStorage.value = rules
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    accessToken: getAccessToken(), // อ่านจาก localStorage
    refreshToken: getRefreshToken(), // อ่านจาก localStorage
    user: getUserData(), // อ่านจาก localStorage
    sessionId: getSessionId(), // อ่านจาก localStorage
    isForceLogoutModalVisible: false,
    isLoggingOut: false, // Flag เพื่อป้องกัน logout ซ้ำ
  }),

  getters: {
    isAuthenticated: state => !!state.accessToken && !!state.user,
  },

  actions: {
    // Initialize abilities from localStorage when app starts
    initializeAbilities() {
      const rules = getAbilityRules()
      if (rules && rules.length > 0)
        ability.update(rules)
    },

    async login(credentials: { email: string; password: string }) {
      try {
        // สร้าง reference ไปยัง cookie ของ accessToken เพื่อให้จัดการได้ง่าย
        // 1. Login to get access token
        const loginData = await $api('/auth/login', { method: 'POST', body: credentials })

        // บันทึก token ลงใน state และ localStorage
        this.accessToken = loginData.access_token
        setAccessToken(loginData.access_token)

        // บันทึก refresh token
        if (loginData.refresh_token) {
          this.refreshToken = loginData.refresh_token
          setRefreshToken(loginData.refresh_token)
        }

        // บันทึก sessionId
        if (loginData.sessionId) {
          this.sessionId = loginData.sessionId
          setSessionId(loginData.sessionId)
        }

        // รอให้ cookie ถูก set เสร็จก่อนเรียก API
        await new Promise(resolve => setTimeout(resolve, 0))

        // 2. Fetch user profile and permissions
        // API /users/me returns { userData: User, rules: Rule[] }
        const meData = await $api('/users/me')

        // 3. Update user state in Pinia and localStorage
        const userData = meData.userData

        // Backend returns full URL for avatar, no need to manipulate
        this.user = userData

        // เก็บข้อมูล user ใน localStorage (ขนาดใหญ่ได้)
        setUserData(userData)

        // 4. Update CASL abilities and persist rules in localStorage
        const rules = meData.rules

        ability.update(rules)
        setAbilityRules(rules)

        // 5. เชื่อมต่อ WebSocket และเริ่มดักฟัง Event
        // เรียก initSocketListeners เสมอเพื่อให้สามารถรับ force_logout event ได้
        this.initSocketListeners()

        // 6. Initialize token manager for proactive refresh
        tokenManager.initialize()

        // 7. Return login result including session info
        return {
          access_token: loginData.access_token,
          sessionId: loginData.sessionId,
          hasExistingSession: loginData.hasExistingSession,
          existingSessionInfo: loginData.existingSessionInfo,
        }
      }
      catch (error) {
        authLogger.error('Login failed:', error)

        // ไม่ต้อง logout เพราะจะทำให้ refresh token เลย
        throw error
      }
    },

    /**
     * ✅ Refresh user data from server and update localStorage
     * เรียกใช้เมื่อ Admin แก้ไขข้อมูล user และต้องการให้ user update ทันที
     */
    async refreshUserData() {
      try {
        // Fetch fresh user data from backend
        const meData = await $api('/users/me')

        // Update user state
        const userData = meData.userData
        this.user = userData
        setUserData(userData)

        // Update CASL abilities
        const rules = meData.rules
        ability.update(rules)
        setAbilityRules(rules)

        authLogger.info('User data refreshed successfully')
      }
      catch (error: any) {
        authLogger.error('Failed to refresh user data:', error)

        // ถ้า refresh ไม่สำเร็จ (เช่น token invalid) ให้ logout
        if (error?.statusCode === 401)
          await this.logout()
      }
    },

    async refreshAccessToken() {
      try {
        // ตรวจสอบว่ามี refresh token หรือไม่
        if (!this.refreshToken || !this.sessionId) {
          authLogger.error('No refresh token available')
          
          // Return false และปล่อยให้ tokenManager.clearTokens() จัดการ logout
          return false
        }

        const data = await $api('/auth/refresh', {
          method: 'POST',
          body: {
            refresh_token: this.refreshToken,
            sessionId: this.sessionId,
          },
        })

        // อัปเดต tokens
        this.accessToken = data.access_token
        setAccessToken(data.access_token)

        if (data.refresh_token) {
          this.refreshToken = data.refresh_token
          setRefreshToken(data.refresh_token)
        }

        // ✅ Reconnect WebSocket with new token to update session
        authLogger.debug('Reconnecting WebSocket with new token after refresh...')
        this.disconnectSocket()
        
        // Wait a bit for disconnect to complete
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Reconnect with new token
        this.initSocketListeners()

        return true
      }
      catch (error) {
        authLogger.error('Failed to refresh access token:', error)
        
        // Return false และปล่อยให้ tokenManager.clearTokens() จัดการ logout
        return false
      }
    },

    async logout(skipRedirect = false) {
      // ป้องกันการ logout ซ้ำ
      if (this.isLoggingOut) {
        authLogger.debug('logout() already in progress, skipping...')
        return
      }

      this.isLoggingOut = true
      authLogger.debug('logout() called - processing logout', { skipRedirect })
      
      // เก็บค่า isForceLogoutModalVisible ไว้ก่อน เพราะ $reset() จะลบทิ้ง
      const shouldShowModal = this.isForceLogoutModalVisible
      
      try {
        // Call logout API if we have a token
        if (this.accessToken) {
          try {
            // ตรวจสอบว่าเป็น temporary token หรือไม่
            const tokenPayload = JSON.parse(atob(this.accessToken.split('.')[1]))
            const sessionId = tokenPayload.sessionId
            const isTemporary = tokenPayload.isTemporary

            // ถ้าเป็น temporary token (concurrent login) ไม่ต้องส่ง sessionId ไป logout
            // เพราะ session เก่ายัง active อยู่
            if (sessionId && !isTemporary) {
              // ✅ Logout API call with special error handling
              // ใช้ fetch แบบ raw แทน $api เพื่อหลีกเลี่ยง auto-refresh mechanism
              const token = tokenManager.getAccessToken()
              
              try {
                await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/auth/logout`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                  },
                  body: JSON.stringify({ sessionId }),
                })
              }
              catch (logoutError) {
                // ✅ Ignore all errors from logout endpoint
                // Token อาจจะ expired แล้ว (401) หรือ network error
                // ไม่ว่าจะเกิดอะไร ให้ proceed ไป clear local state
              }
            }
            else if (isTemporary) {
              // Temporary token detected, skipping logout API call
            }
          }
          catch (decodeError) {
            console.warn('Failed to decode token during logout (token may be expired/invalid):', decodeError)
            // Continue with logout cleanup anyway
          }
        }
      }
      catch (error) {
        console.warn('Logout process error (ignored):', error)
        // Continue with cleanup anyway
      }
      finally {
        // ตัดการเชื่อมต่อ WebSocket และ cleanup event listeners
        this.disconnectSocket()
        window.removeEventListener('force_logout', this.handleForceLogout)
        window.removeEventListener('user-data-updated', this.handleUserDataUpdated)

        // Clear all intervals and timeouts globally
        this.clearAllTimers()

        // Clear storage first (ก่อน $reset เพราะต้องใช้ accessToken ใน try block)
        setAccessToken(null) // ลบ token ออกจาก localStorage
        setRefreshToken(null) // ลบ refresh token ออกจาก localStorage
        setUserData(null) // ลบข้อมูล user ออกจาก localStorage
        setSessionId(null) // ลบ sessionId ออกจาก localStorage

        // Clear CASL abilities and localStorage
        ability.update([])
        setAbilityRules([])

        // Clear sessionStorage (including wait timer state)
        sessionStorage.removeItem('callwork_wait_timer')
        sessionStorage.removeItem('currentLead')
        sessionStorage.removeItem('currentCampaign')
        sessionStorage.removeItem('callWorkState')

        // Clear callwork localStorage (selected campaign)
        localStorage.removeItem('callwork_selected_campaign')

        // Clear Pinia state
        this.$reset()
        
        // ถ้ากำลังแสดง force logout modal อยู่ ไม่ต้อง redirect ทันที
        // ปล่อยให้ modal จัดการเอง
        if (shouldShowModal) {
          this.isForceLogoutModalVisible = true
          this.isLoggingOut = false
          return
        }

        // ถ้า skipRedirect = true (เช่น จาก tokenManager เมื่อ refresh token invalid)
        // ให้ skip redirect เพราะ useApi จะจัดการ redirect เอง
        if (skipRedirect) {
          this.isLoggingOut = false
          return
        }
        window.location.href = '/login'
      }
    },

    clearAllTimers() {
      // Use the global timer cleanup function
      clearAllGlobalTimers()
    },

    disconnectSocket() {
      // ตัดการเชื่อมต่อ WebSocket
      const { disconnectSocket } = useSocket()

      disconnectSocket()
    },

    async confirmConcurrentLogin(userId: number) {
      try {
        // เรียก API เพื่อ confirm login และ kick existing session
        const confirmResult = await $api(`/auth/confirm-login/${userId}`, {
          method: 'POST',
        })

        // อัปเดต auth store ด้วย token ใหม่
        if (confirmResult?.access_token) {
          // บันทึก token ลงใน state และ localStorage
          this.accessToken = confirmResult.access_token
          setAccessToken(confirmResult.access_token)

          // บันทึก sessionId
          if (confirmResult.sessionId) {
            this.sessionId = confirmResult.sessionId
            setSessionId(confirmResult.sessionId)
          }

          // รอให้ cookie ถูก set เสร็จก่อนเรียก API
          await new Promise(resolve => setTimeout(resolve, 0))

          // Fetch user profile และ permissions
          const meData = await $api('/users/me')

          // อัปเดต user state
          const userData = meData.userData

          // Backend returns full URL for avatar, no need to manipulate
          this.user = userData

          // เก็บข้อมูล user ใน localStorage (ขนาดใหญ่ได้)
          setUserData(userData)

          // อัปเดต CASL abilities
          const rules = meData.rules

          ability.update(rules)
          setAbilityRules(rules)

          // เชื่อมต่อ WebSocket
          this.initSocketListeners()

          // Initialize token manager for proactive refresh
          tokenManager.initialize()
        }

        return confirmResult
      }
      catch (error) {
        console.error('❌ Confirm login failed:', error)
        throw error
      }
    },

    initSocketListeners() {
      if (!this.accessToken)
        return

      const { connectSocket } = useSocket()

      // เชื่อมต่อ WebSocket (improved version จะจัดการ event listeners ทั้งหมดเอง)
      connectSocket(this.accessToken)

      // ✅ ฟัง force_logout event จาก window (ป้องกัน circular dependency)
      window.addEventListener('force_logout', this.handleForceLogout)

      // ✅ ฟัง user-data-updated event เพื่อ refresh user data แบบ real-time
      window.addEventListener('user-data-updated', this.handleUserDataUpdated)

      // ✅ ฟัง rolePermissionsUpdated event เพื่อ refresh user data เมื่อ role permissions ถูกแก้ไข
      window.addEventListener('rolePermissionsUpdated', this.handleRolePermissionsUpdated)

    },

    handleForceLogout() {
      // ปิด WebSocket connection ก่อนแสดง modal
      this.disconnectSocket()

      // แสดง modal แจ้งเตือน
      this.showForceLogoutModal()
    },

    /**
     * ✅ Handle user-data-updated event from WebSocket
     * เรียกเมื่อ Admin แก้ไขข้อมูล user และต้องการให้ user update ทันที
     */
    handleUserDataUpdated(event: Event) {
      const customEvent = event as CustomEvent

      // Refresh user data from server
      this.refreshUserData()
    },

    /**
     * ✅ Handle role permissions updated event
     * เมื่อ Admin แก้ไข role permissions, users ที่เป็น member จะได้รับ event นี้
     */
    handleRolePermissionsUpdated(event: Event) {
      const customEvent = event as CustomEvent
      const { roleId } = customEvent.detail

      authLogger.info(`🔐 Role permissions updated (roleId: ${roleId}), refreshing user data...`)

      // Refresh user data to get updated permissions
      this.refreshUserData()
    },

    showForceLogoutModal() {
      this.isForceLogoutModalVisible = true
    },

    async closeForceLogoutModal() {
      
      // ปิด modal ก่อน
      this.isForceLogoutModalVisible = false
      
      // Reset isLoggingOut เพื่อให้ logout() ทำงานได้
      this.isLoggingOut = false
      
      // เรียก logout อีกครั้ง (ครั้งนี้ isForceLogoutModalVisible = false แล้ว จะ redirect จริงๆ)
      await this.logout()
    },

    /**
     * Initializes authentication state and reconnects services.
     * This should be called when the application starts.
     */
    initializeAuth() {
      if (this.isAuthenticated) {
        this.initSocketListeners()
        tokenManager.initialize()
      }
    },
  },
})
